import { useEffect, useMemo, useState } from 'react';
import { parseLocalToUTC } from '@safetyhub/shared';
import {
  Button,
  DateInput,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  FORM_TEXTAREA_CLASS,
  Modal,
} from '@safetyhub/ui';
import { MODAL_LAYER } from '../../../components/modalLayers';
import { organizationsApi, type OrganizationMember } from '../../../lib/organizations';
import type { CompanyRoleView, RoleAssignmentView } from '../../../lib/rbac';
import type { CreateRoleAssignmentDto } from '../../../lib/rbac';

/** 배정 후보 — 조직 멤버 목록(`GET /organizations/:id/members`)의 행을 Member 단위로 편 것. */
export interface AssignableMember {
  /** Member.id — 배정 대상 식별자(CompanyMember.id 아님). */
  memberId: string;
  name: string;
  contact: string | null;
  hasAccount: boolean;
}

/**
 * 조직 멤버 목록 행 → 배정 후보. `member.id`가 정본이고(`memberId`는 같은 값의 평면 사본), 둘 다 없는
 * 행(형상 불일치)과 탈퇴한 Member는 제외한다. 계정 없는 외부 연락처 행(`user` null)도 후보다 —
 * `allowsNoAccount=false` 역할에서는 호출부가 건다.
 */
export function toAssignableMembers(rows: OrganizationMember[]): AssignableMember[] {
  const out: AssignableMember[] = [];
  for (const row of rows) {
    const memberId = row.member?.id ?? row.memberId;
    if (!memberId) continue;
    if (row.member?.deletedAt) continue;
    const name = row.member?.name ?? row.user?.name ?? '(이름 없음)';
    const contact = row.member?.phone ?? row.member?.email ?? row.user?.phone ?? null;
    out.push({ memberId, name, contact, hasAccount: !!(row.user && !row.user.deletedAt) });
  }
  return out;
}

/** 오늘을 `<input type="date">` 값(로컬 날짜)으로. */
function todayLocalDateInput(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

interface AssignRoleModalProps {
  open: boolean;
  role: CompanyRoleView | null;
  companyId: string;
  /** 이 역할의 현재 보유자 — 후보에서 뺀다(서버 409 `ROLE_ASSIGNMENT_ALREADY_EXISTS`를 화면에서 먼저 막는다). */
  currentHolders: RoleAssignmentView[];
  isSubmitting: boolean;
  overlayZClassName?: string;
  onSubmit: (payload: CreateRoleAssignmentDto) => Promise<void>;
  onClose: () => void;
}

/**
 * 직위 배정 모달 — 멤버 선택·선임일·전문기관 위탁·메모. 생성 모드라 dirty 게이트 비대상(결정
 * `2026-08-16-edit-modal-dirty-gate-standard` ④).
 *
 * 멤버 재료는 조직 멤버 목록 하나다(계정 없는 외부 연락처 포함). 직접 입력(신규 Member 생성)은 이 화면에
 * 두지 않는다 — 외부 인원 등록은 현장 담당자·결재선 편집 경로가 이미 맡는다.
 */
export function AssignRoleModal({
  open,
  role,
  companyId,
  currentHolders,
  isSubmitting,
  overlayZClassName = MODAL_LAYER.root,
  onSubmit,
  onClose,
}: AssignRoleModalProps) {
  const [members, setMembers] = useState<AssignableMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [memberId, setMemberId] = useState('');
  const [assignedAt, setAssignedAt] = useState(todayLocalDateInput);
  const [delegated, setDelegated] = useState(false);
  const [agencyName, setAgencyName] = useState('');
  const [note, setNote] = useState('');

  // 열릴 때마다 초기화 + 멤버 목록 로드(조직 전환·다른 역할로 다시 열 때 이전 선택이 남지 않게).
  useEffect(() => {
    if (!open) return;
    setMemberId('');
    setAssignedAt(todayLocalDateInput());
    setDelegated(false);
    setAgencyName('');
    setNote('');
    let cancelled = false;
    setIsLoadingMembers(true);
    setLoadError('');
    organizationsApi
      .getMembers(companyId)
      .then((rows) => {
        if (!cancelled) setMembers(toAssignableMembers(rows));
      })
      .catch(() => {
        if (!cancelled) setLoadError('멤버 목록을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingMembers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const holderIds = useMemo(() => new Set(currentHolders.map((h) => h.memberId)), [currentHolders]);
  // 이미 보유자는 뺀다. 계정 없는 행은 `allowsNoAccount=false` 역할에서 **비활성+사유**로 남긴다(서버 400
  // `ROLE_ASSIGNMENT_ACCOUNT_REQUIRED`를 화면이 먼저 막되, 왜 못 고르는지는 보이게).
  const candidates = useMemo(() => members.filter((m) => !holderIds.has(m.memberId)), [members, holderIds]);
  const requiresAccount = !!role && !role.allowsNoAccount;
  const isSelectable = (m: AssignableMember) => !requiresAccount || m.hasAccount;
  const selectedMember = candidates.find((m) => m.memberId === memberId);

  const canSubmit =
    !!role &&
    !!selectedMember &&
    isSelectable(selectedMember) &&
    !!assignedAt &&
    (!delegated || agencyName.trim() !== '') &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!role || !canSubmit) return;
    const payload: CreateRoleAssignmentDto = {
      memberId,
      roleKey: role.key,
      // 날짜만 고르는 입력 — 로컬 00:00 → UTC(CLAUDE.md 시간대 정책).
      assignedAt: parseLocalToUTC(`${assignedAt}T00:00:00`).toISOString(),
      delegatedToAgency: delegated,
    };
    if (delegated) payload.agencyName = agencyName.trim();
    if (note.trim()) payload.note = note.trim();
    await onSubmit(payload);
  };

  return (
    <Modal
      open={open && !!role}
      onClose={onClose}
      title={role ? `${role.name} 배정` : '배정'}
      size="md"
      overlayZClassName={overlayZClassName}
      closeLocked={isSubmitting}
      footer={
        <>
          <Button type="button" onClick={onClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? '배정 중...' : '배정'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <div>
          <label htmlFor="assign-member" className={FORM_LABEL_CLASS}>
            인원 <span className="text-rose-500">*</span>
          </label>
          <select
            id="assign-member"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            className={FORM_CONTROL_CLASS}
            disabled={isLoadingMembers || isSubmitting}
          >
            <option value="">{isLoadingMembers ? '불러오는 중...' : '선택하세요'}</option>
            {candidates.map((m) => {
              const selectable = isSelectable(m);
              return (
                <option key={m.memberId} value={m.memberId} disabled={!selectable}>
                  {m.name}
                  {m.contact ? ` · ${m.contact}` : ''}
                  {m.hasAccount ? '' : selectable ? ' · 계정 없음' : ' · 계정이 있는 멤버만'}
                </option>
              );
            })}
          </select>
          {loadError && <p className="mt-1 text-xs text-rose-600">{loadError}</p>}
          {!isLoadingMembers && !loadError && candidates.filter(isSelectable).length === 0 && (
            <p className="mt-1 text-xs text-ink-500">배정할 수 있는 인원이 없습니다.</p>
          )}
          {requiresAccount && (
            <p className="mt-1 text-xs text-ink-500">이 역할은 계정이 필요합니다. 계정 없는 인원은 초대를 보내 가입·수락한 뒤 배정해 주세요.</p>
          )}
        </div>

        <div>
          <label htmlFor="assign-date" className={FORM_LABEL_CLASS}>
            선임일 <span className="text-rose-500">*</span>
          </label>
          <DateInput
            label="선임일"
            id="assign-date"
            value={assignedAt}
            onChange={setAssignedAt}
            disabled={isSubmitting}
            // `FORM_CONTROL_CLASS` 의 고정 높이·굵기를 **명시해 넘긴다** — 공용 `md` 는 패딩으로
            // 높이를 정해 42px 이 아니고, 이 폼의 형제 칸들(위 select·아래 입력)이 전부 그
            // 상수를 쓰므로 그대로 두면 날짜 줄만 낮아 줄이 어긋난다. `md` 에 높이·굵기
            // 클래스가 없어 충돌이 없다(반경 `rounded-control` 은 10px 로 그 상수와 같은 값,
            // 테두리도 양쪽 `border-line` 이라 손댈 것이 없다).
            className="w-full h-[42px] font-semibold"
          />
        </div>

        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={delegated}
              onChange={(e) => setDelegated(e.target.checked)}
              className="h-4 w-4 rounded border-line text-brand-600"
              disabled={isSubmitting}
            />
            전문기관에 위탁
          </label>
          {delegated && (
            <div>
              <label htmlFor="assign-agency" className={FORM_LABEL_CLASS}>
                위탁 기관명 <span className="text-rose-500">*</span>
              </label>
              <input
                id="assign-agency"
                type="text"
                value={agencyName}
                onChange={(e) => setAgencyName(e.target.value)}
                className={FORM_CONTROL_CLASS}
                placeholder="예: OO안전관리전문기관"
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>

        <div>
          <label htmlFor="assign-note" className={FORM_LABEL_CLASS}>
            메모 (선택)
          </label>
          <textarea
            id="assign-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={FORM_TEXTAREA_CLASS}
            rows={2}
            disabled={isSubmitting}
          />
        </div>
      </form>
    </Modal>
  );
}
